import { getApiErrorDetail, useApiClient } from '@klareospace/pharos-lib-front-utils';
import { CARRIER_CASCADE_MODE } from '@klareospace/pharos-lib-types';
import type { Carrier, CarrierCascadeMode } from '@klareospace/pharos-lib-types';
import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { driverKeys } from '../../drivers/api';
import { vehicleKeys } from '../../vehicles/api';
import { carrierKeys, createCarrierApi } from '../api';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ArchiveCarrierVariables {
  cascadeMode?: CarrierCascadeMode;
  id: string;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useArchiveCarrier(): UseMutationResult<Carrier, Error, ArchiveCarrierVariables> {
  const client = useApiClient('admin-auth');
  const api = createCarrierApi(client);
  const queryClient = useQueryClient();
  const { t } = useTranslation('settings');

  return useMutation<Carrier, Error, ArchiveCarrierVariables>({
    mutationFn: ({ cascadeMode, id }: ArchiveCarrierVariables) => api.archiveCarrier(id, cascadeMode),
    onError: (error: Error) => {
      const detail = getApiErrorDetail(error);
      if (detail.errorCode === 'Conflict.CarrierStillReferenced') {
        toast.error(t('spaces.settings.carriers.toast.archiveErrorReferenced'));
        return;
      }
      toast.error(detail.message || t('spaces.settings.carriers.toast.archiveError'));
    },
    onSuccess: (_result, variables) => {
      toast.success(t('spaces.settings.carriers.toast.archived'));
      void queryClient.invalidateQueries({ queryKey: carrierKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: carrierKeys.detail(variables.id) });
      if (variables.cascadeMode === CARRIER_CASCADE_MODE.CASCADE) {
        void queryClient.invalidateQueries({ queryKey: driverKeys.all });
        void queryClient.invalidateQueries({ queryKey: vehicleKeys.all });
      }
    },
  });
}
